/**
 * File Upload Component
 *
 * shadcn/ui Input 컴포넌트를 확장한 파일 업로드 컴포넌트
 * Figma 디자인에 맞춘 커스텀 Upload File 버튼 제공
 */

import { Upload } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/shared/lib/utils/classnames';
import { Button } from './button';

export interface FileUploadProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  error?: boolean;
  errorMessage?: string;
  onFileChange?: (file: File | null) => void;
}

export const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  ({ className, error, errorMessage, onFileChange, onChange, disabled, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = React.useState('');

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const triggerFileSelect = () => inputRef.current?.click();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setFileName(file?.name ?? '');
      onFileChange?.(file);
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        triggerFileSelect();
      }
    };

    const containerBase = 'flex h-12 rounded-[4px] border bg-white';

    return (
      <div className="flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          aria-invalid={error}
          disabled={disabled}
          onChange={handleChange}
          {...props}
        />

        {!fileName ? (
          <button
            type="button"
            className={cn(
              containerBase,
              'justify-center px-3 border-dashed border-label-weakest items-center',
              !disabled && 'hover:bg-gray-50 cursor-pointer',
              error && 'border-red-500',
              className,
            )}
            onClick={disabled ? undefined : triggerFileSelect}
            onKeyDown={disabled ? undefined : handleKeyDown}
            disabled={disabled}
            aria-label="Upload file"
          >
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              tabIndex={-1}
              className="h-6 w-24 px-2 text-xs font-medium text-label-neutral flex items-center gap-0.5 bg-transparent pointer-events-none"
            >
              <Upload className="w-4 h-4" />
              Upload File
            </Button>
          </button>
        ) : (
          <div className={cn(containerBase, 'items-center px-4 border-[#D9DDE0]')}>
            <span className="flex-1 text-sm text-foreground truncate">{fileName}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={triggerFileSelect}
              disabled={disabled}
              className="h-8 px-3 ml-3 text-xs font-medium border-[#D9DDE0] hover:bg-gray-50"
            >
              파일 변경
            </Button>
          </div>
        )}

        {error && errorMessage && <p className="text-sm text-red-500">{errorMessage}</p>}
      </div>
    );
  },
);

FileUpload.displayName = 'FileUpload';
